// src/components/recipient/RecipientLetter.tsx

import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { LetterConfig } from "../../types/recipient.types";
import lrnsy_letter_envelope from "../../assets/img/archetypes/lrnsy_letter_envelope.png";

interface ExtendedLetterConfig extends LetterConfig {
  primaryColor?: string;
  secondaryColor?: string;
  hasBouquet?: boolean;
  lines?: string[];
  theme?: any;
  bgImage?: string;
  archetypeName?: string;
  image?: string;
}

interface RecipientLetterProps {
  config: ExtendedLetterConfig;
  senderName: string;
  recipientName: string;
  onOpen: () => void;
}

export const RecipientLetter: React.FC<RecipientLetterProps> = ({
  config,
  senderName,
  recipientName,
  onOpen,
}) => {
  const [stage, setStage] = useState<"envelope" | "opening" | "reading">("envelope");
  const [typedText, setTypedText] = useState("");
  const [isReadyToContinue, setIsReadyToContinue] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const primary = config.primaryColor || "#9c6d57";
  const secondary = config.secondaryColor || "#5d4b3b";
  const hasBouquet = config.hasBouquet ?? false;

  console.log("config: ", config);

  const letterContent = useMemo(() => {
    const lines = config.lines?.length
      ? config.lines
      : config.content
        ? config.content.split("\n").filter((l) => l.trim() !== "")
        : [];

    if (lines.length === 0) {
      return `I wanted to take a moment to write to you, not through a quick message or a text, but something more intentional, something you can feel.\n\nYou mean more to me than words can easily capture, and yet, here I am, trying.\n\nThank you for being exactly who you are. In a world that moves too fast, you're the pause I didn't know I needed.\n\nWith all my heart,`;
    }
    return lines.join("\n\n");
  }, [config.content, config.lines]);

  // Handle Scroll logic to hide/show indicator
  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    // If we are within 20px of the bottom, hide the indicator
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 20;
    setShowScrollIndicator(!isAtBottom);
  };

  useEffect(() => {
    if (stage === "opening") {
      const t = setTimeout(() => setStage("reading"), 1200);
      return () => clearTimeout(t);
    }
  }, [stage]);

  useEffect(() => {
    if (stage !== "reading") return;
    let index = 0;
    const speed = 28;
    const typer = setInterval(() => {
      index++;
      setTypedText(letterContent.slice(0, index));

      // Check if scroll is needed as text expands
      if (contentRef.current) {
        const isScrollable = contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setShowScrollIndicator(isScrollable);
      }

      if (index >= letterContent.length) {
        clearInterval(typer);
        setTimeout(() => setIsReadyToContinue(true), 900);
      }
    }, speed);
    return () => clearInterval(typer);
  }, [stage, letterContent]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center z-[100] overflow-hidden"
      style={{
        background:
          stage === "reading"
            ? "#000"
            : `radial-gradient(circle at center, ${primary}82 20%, ${secondary}22 70%, #000 100%)`,
      }}
    >
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* READING BACKGROUND IMAGE */}
      <AnimatePresence>
        {stage === "reading" && (
          <motion.div
            key="bgImage"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 z-0"
          >
            <img
              src={config.bgImage || lrnsy_letter_envelope}
              alt="Background"
              className="w-full h-full object-cover"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className="absolute inset-0 pointer-events-none z-[1] "
        style={{
          background: `
            linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 30%, rgba(0,0,0,0.15) 60%, rgba(0,0,0,0.55)),
            radial-gradient(circle at center, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.75) 100%)
          `,
        }}
      />

      <AnimatePresence mode="wait">
        {stage === "envelope" && (
          <motion.div
            key="envelope"
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.1, filter: "blur(20px)" }}
            className="flex flex-col items-center gap-10 text-center z-10 px-4"
          >
            <div className="space-y-3">
              <p className="uppercase tracking-[.4em] text-[10px] md:text-xs text-white/60">
                A LRNSY Letter
              </p>
              <p className="text-white/80 font-serif italic text-lg md:text-xl">
                For {recipientName}
              </p>
            </div>
            <motion.button
              onClick={() => setStage("opening")}
              whileHover={{ scale: 1.03, y: -12, rotateX: 5, rotateY: -2 }}
              className="group relative w-[280px] h-[180px] md:w-[400px] md:h-[260px] rounded-2xl transition-all duration-500 preserve-3d"
              style={{ perspective: "1000px" }}
            >
              <div className="absolute inset-x-4 -bottom-6 h-8 bg-black/40 blur-2xl rounded-[50%]" />
              <div className="relative w-full h-full rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
                <img
                  src={lrnsy_letter_envelope}
                  alt="Envelope"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                />
                <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)]" />
              </div>
              <div className="mt-4">
                <span className="text-[10px] uppercase tracking-[.4em] font-bold text-white/80">
                  Tap To Open
                </span>
              </div>
            </motion.button>
          </motion.div>
        )}

        {stage === "reading" && (
          <motion.div
            key="reading"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{
              opacity: { duration: 1.4 },
              y: { duration: 14, ease: "easeInOut", repeat: Infinity },
            }}
            className="relative w-full max-w-3xl mx-6 z-20 h-full max-h-[85vh] flex flex-col"
          >
            {/* Background Image Layer */}
            {config.image && (
              <motion.div
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 0.4, scale: 1 }}
                transition={{ duration: 2.5 }}
                className="absolute inset-0 -z-20 rounded-[40px] overflow-hidden"
              >
                <img src={config.image} alt="" className="w-full h-full object-cover" />
                {/* Additional gradient overlay on top of image for better text contrast */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
              </motion.div>
            )}

            {/* LUXURY READING SURFACE - now with adjusted opacity if image exists */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 -z-10 rounded-[40px]"
              style={{
                backdropFilter: "blur(6px)",
                background: config.image
                  ? `linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.35) 25%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.45))`
                  : `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.45) 25%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.55)),
             radial-gradient(circle at center, rgba(0,0,0,0.25), rgba(0,0,0,0.6))`,
                boxShadow: "0 60px 120px rgba(0,0,0,0.6), inset 0 0 120px rgba(0,0,0,0.35)",
              }}
            />
            {/* AMBIENT LIGHT & TEXTURE (Same as before) */}
            <motion.div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-[40px] pointer-events-none"
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 28, ease: "easeInOut", repeat: Infinity }}
              style={{
                background: `radial-gradient(circle at 30% 40%, rgba(255,255,255,0.08), transparent 55%), radial-gradient(circle at 70% 60%, rgba(255,220,190,0.06), transparent 60%)`,
                backgroundSize: "200% 200%",
                mixBlendMode: "soft-light",
              }}
            />
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-[40px] pointer-events-none opacity-[0.06]"
              style={{
                backgroundImage: `repeating-linear-gradient(0deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 3px)`,
                mixBlendMode: "overlay",
              }}
            />

            {/* SCROLLABLE CONTENT AREA */}
            <div
              ref={contentRef}
              onScroll={handleScroll}
              className="relative px-6 sm:px-10 md:px-16 py-16 overflow-y-auto hide-scrollbar h-full"
            >
              {/* HEADER */}
              <div className="flex justify-between text-white/50 text-xs mb-12 uppercase tracking-widest font-medium">
                <span className="italic font-serif">
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span className="text-[8px]">LRNSY : {config.archetypeName || "The Letter"}</span>
              </div>

              <p className="font-serif italic text-white/95 text-lg md:text-xl mb-10">
                Dear {recipientName},
              </p>

              <p className="font-serif whitespace-pre-line leading-[1.9] tracking-[0.02em] text-white/95 text-[16px] md:text-[20px]">
                {typedText}
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="inline-block w-[2px] h-5 ml-1 align-middle"
                  style={{ background: "rgba(255,255,255,0.8)" }}
                />
              </p>

              {/* SIGNATURE */}
              {isReadyToContinue && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col mt-8 w-full"
                >
                  <div className="flex flex-col items-end w-full">
                    <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] mb-2">
                      With love and sincerity,
                    </p>
                    <p
                      className="font-serif italic text-4xl text-right w-full"
                      style={{ color: primary, letterSpacing: "0.04em" }}
                    >
                      {senderName}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ACTION BUTTON */}
              {isReadyToContinue && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center mt-16 pb-8"
                >
                  <button onClick={onOpen} className="group flex flex-col items-center gap-2">
                    <span className="font-serif italic text-lg text-white/80 group-hover:text-white transition-colors">
                      {hasBouquet ? "Unveil your Flowers" : "Complete your Journey"}
                    </span>
                    <ArrowRight
                      className="text-white/40 group-hover:translate-x-1 transition-transform"
                      size={20}
                    />
                  </button>
                </motion.div>
              )}
            </div>

            {/* FLOATING SCROLL INDICATOR */}
            <AnimatePresence>
              {showScrollIndicator && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute bottom-6 -ml-10 left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none z-50"
                >
                  <span
                    className="text-[10px]  uppercase tracking-[0.3em] text-white/40 mb-1 block text-center"
                    style={{
                      marginRight: "-0.3em",
                    }}
                  >
                    Scroll Down
                  </span>

                  <motion.div
                    animate={{ y: [0, 5, 0] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="flex justify-center w-full"
                  >
                    <ChevronDown size={14} className="text-white/40" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
